import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import {
  Users,
  Activity,
  CreditCard,
  TrendingUp,
  FileText,
  DollarSign,
  ArrowRight,
  RefreshCw,
} from "lucide-react";

interface Stats {
  totalUsers: number;
  activeToday: number;
  totalLogins: number;
  totalToolUsage: number;
  totalRevenue: number;
  totalPaidUsers: number;
  totalResumes: number;
  totalJobs: number;
}

interface RecentTransaction {
  id: string;
  user_email: string;
  action_type: string;
  credits_change: number;
  created_at: string;
}

export default function AdminOverview() {
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    activeToday: 0,
    totalLogins: 0,
    totalToolUsage: 0,
    totalRevenue: 0,
    totalPaidUsers: 0,
    totalResumes: 0,
    totalJobs: 0,
  });
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchOverviewData = async () => {
    setLoading(true);
    try {
      // Fetch admin user IDs to exclude them from transactions
      const { data: adminRoles } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "admin");

      const adminUserIds = new Set((adminRoles || []).map((r) => r.user_id));

      // Fetch profiles
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, last_seen_at, created_at");

      // Fetch activity logs with created_at for date-based filtering
      const { data: activityLogs } = await supabase
        .from("user_activity_logs")
        .select("activity_type, user_id, created_at")
        .order("created_at", { ascending: false });

      // Fetch credit transactions for tool usage and recent activity (fetch more to filter)
      const { data: transactions } = await supabase
        .from("credit_transactions")
        .select("id, user_id, action_type, credits_change, created_at")
        .order("created_at", { ascending: false })
        .limit(50);

      // Fetch profiles for transaction emails
      const { data: profilesForTx } = await supabase
        .from("profiles")
        .select("id, email");

      // Fetch payments
      const { data: payments } = await supabase
        .from("user_payments")
        .select("user_id, amount_cents, payment_status")
        .eq("payment_status", "completed");

      // Fetch user_credits with plan info to identify paid users
      const { data: userCreditsData } = await supabase
        .from("user_credits")
        .select("user_id, plan_id");

      // Fetch subscription plans to know which are free
      const { data: plansData } = await supabase
        .from("subscription_plans")
        .select("id, is_free");

      // Fetch tailored resume counts (resumes with original_resume_id or original_match_score = tailored)
      const { data: resumes } = await supabase.from("resumes").select("id, user_id, original_resume_id, original_match_score");
      const { data: jobs } = await supabase.from("saved_job_descriptions").select("id, user_id");

      // Calculate stats (excluding admin users)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Filter out admin profiles
      const memberProfiles = profiles?.filter((p) => !adminUserIds.has(p.id)) || [];
      
      // Calculate "Active Today" based on activity logs, transactions, AND last_seen_at
      const todayActivityUserIds = new Set(
        (activityLogs || [])
          .filter((l) => {
            const logDate = new Date(l.created_at);
            logDate.setHours(0, 0, 0, 0);
            return logDate.getTime() === today.getTime() && !adminUserIds.has(l.user_id);
          })
          .map((l) => l.user_id)
      );

      const todayTxUserIds = new Set(
        (transactions || [])
          .filter((tx) => {
            const txDate = new Date(tx.created_at);
            txDate.setHours(0, 0, 0, 0);
            return txDate.getTime() === today.getTime() && !adminUserIds.has(tx.user_id);
          })
          .map((tx) => tx.user_id)
      );

      // Also check profiles.last_seen_at for today
      const todaySeenUserIds = new Set(
        memberProfiles
          .filter((p) => {
            if (!p.last_seen_at) return false;
            const seenDate = new Date(p.last_seen_at);
            seenDate.setHours(0, 0, 0, 0);
            return seenDate.getTime() === today.getTime();
          })
          .map((p) => p.id)
      );

      const activeToday = new Set([...todayActivityUserIds, ...todayTxUserIds, ...todaySeenUserIds]).size;

      // Filter out admin activity logs
      const memberActivityLogs = activityLogs?.filter((l) => !adminUserIds.has(l.user_id)) || [];
      const loginCount = memberActivityLogs.filter((l) => l.activity_type === "login").length;
      
      // Tool usage comes from credit_transactions (actual tool usage with credit deductions)
      const memberTransactions = transactions?.filter((tx) => !adminUserIds.has(tx.user_id)) || [];
      const toolUsageCount = memberTransactions.filter((tx) => tx.credits_change < 0).length;

      // Filter out admin payments for revenue
      const memberPayments = payments?.filter((p) => !adminUserIds.has(p.user_id)) || [];
      const totalRevenue = memberPayments.reduce((sum, p) => sum + p.amount_cents, 0);

      // Paid users = users on a non-free plan (from user_credits + subscription_plans)
      const freePlanIds = new Set((plansData || []).filter((p) => p.is_free).map((p) => p.id));
      const paidUserCount = (userCreditsData || []).filter(
        (uc) => !adminUserIds.has(uc.user_id) && uc.plan_id && !freePlanIds.has(uc.plan_id)
      ).length;

      // Filter out admin resumes and count tailored resumes (those with original_resume_id or original_match_score)
      const memberResumes = resumes?.filter((r) => 
        !adminUserIds.has(r.user_id) && 
        (r.original_resume_id != null || r.original_match_score != null)
      ) || [];
      const memberJobs = jobs?.filter((j) => !adminUserIds.has(j.user_id)) || [];

      setStats({
        totalUsers: memberProfiles.length,
        activeToday,
        totalLogins: loginCount,
        totalToolUsage: toolUsageCount,
        totalRevenue,
        totalPaidUsers: paidUserCount,
        totalResumes: memberResumes.length,
        totalJobs: memberJobs.length,
      });

      // Filter out admin users and orphan records, then map transactions with emails
      const txWithEmails = (transactions || [])
        .filter((tx) => !adminUserIds.has(tx.user_id))
        .filter((tx) => profilesForTx?.some((p) => p.id === tx.user_id)) // Exclude orphans
        .slice(0, 10)
        .map((tx) => {
          const profile = profilesForTx?.find((p) => p.id === tx.user_id);
          return {
            ...tx,
            user_email: profile?.email || "Unknown",
          };
        });
      setRecentTransactions(txWithEmails);
    } catch (error) {
      console.error("Error fetching overview data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  };

  const formatActionType = (actionType: string) => {
    return actionType
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-primary/5 via-card to-accent/5 rounded-xl p-6 shadow-sm border border-primary/10">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Dashboard Overview</h1>
          <p className="text-muted-foreground">Welcome back! Here's what's happening.</p>
        </div>
        <Button variant="outline" onClick={fetchOverviewData} disabled={loading} className="bg-background hover:bg-primary/10 hover:text-primary hover:border-primary/30">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-br from-info/5 to-card shadow-sm border-l-4 border-l-info border hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-info/10 ring-2 ring-info/20">
                <Users className="h-6 w-6 text-info" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold text-foreground">{stats.totalUsers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-success/5 to-card shadow-sm border-l-4 border-l-success border hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-success/10 ring-2 ring-success/20">
                <Activity className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Today</p>
                <p className="text-2xl font-bold text-foreground">{stats.activeToday}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-accent/5 to-card shadow-sm border-l-4 border-l-accent border hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-accent/10 ring-2 ring-accent/20">
                <TrendingUp className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Logins</p>
                <p className="text-2xl font-bold text-foreground">{stats.totalLogins}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-warning/5 to-card shadow-sm border-l-4 border-l-warning border hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-warning/10 ring-2 ring-warning/20">
                <DollarSign className="h-6 w-6 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold text-foreground">{formatCurrency(stats.totalRevenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-gradient-to-r from-primary/5 to-card shadow-sm border border-primary/10 hover:border-primary/30 transition-colors">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Resumes</p>
              <p className="text-lg font-semibold text-foreground">{stats.totalResumes}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-success/5 to-card shadow-sm border border-success/10 hover:border-success/30 transition-colors">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <CreditCard className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Paid Users</p>
              <p className="text-lg font-semibold text-foreground">{stats.totalPaidUsers}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-accent/5 to-card shadow-sm border border-accent/10 hover:border-accent/30 transition-colors">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent/10">
              <Activity className="h-5 w-5 text-accent" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tool Usage</p>
              <p className="text-lg font-semibold text-foreground">{stats.totalToolUsage}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-info/5 to-card shadow-sm border border-info/10 hover:border-info/30 transition-colors">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-info/10">
              <FileText className="h-5 w-5 text-info" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Saved Jobs</p>
              <p className="text-lg font-semibold text-foreground">{stats.totalJobs}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Links & Recent Activity */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Quick Actions */}
        <Card className="bg-card shadow-sm border overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-b border-primary/10">
            <CardTitle className="text-foreground flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Activity className="h-4 w-4 text-primary" />
              </div>
              Quick Actions
            </CardTitle>
            <CardDescription>Common admin tasks</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 p-4">
            <Link to="/admin/users">
              <Button variant="outline" className="w-full justify-between bg-gradient-to-r from-success/5 to-transparent border-success/20 hover:bg-success/10 hover:text-success hover:border-success/40 transition-all">
                <span className="flex items-center gap-2">
                  <div className="p-1 rounded bg-success/10">
                    <Users className="h-4 w-4 text-success" />
                  </div>
                  Manage Users
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/admin/billing">
              <Button variant="outline" className="w-full justify-between bg-gradient-to-r from-warning/5 to-transparent border-warning/20 hover:bg-warning/10 hover:text-warning hover:border-warning/40 transition-all">
                <span className="flex items-center gap-2">
                  <div className="p-1 rounded bg-warning/10">
                    <CreditCard className="h-4 w-4 text-warning" />
                  </div>
                  Credits & Plans
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/admin/activity">
              <Button variant="outline" className="w-full justify-between bg-gradient-to-r from-info/5 to-transparent border-info/20 hover:bg-info/10 hover:text-info hover:border-info/40 transition-all">
                <span className="flex items-center gap-2">
                  <div className="p-1 rounded bg-info/10">
                    <Activity className="h-4 w-4 text-info" />
                  </div>
                  View Activity Logs
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card className="bg-card shadow-sm border overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-accent/10 via-accent/5 to-transparent border-b border-accent/10">
            <div>
              <CardTitle className="text-foreground flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-accent/10">
                  <CreditCard className="h-4 w-4 text-accent" />
                </div>
                Recent Transactions
              </CardTitle>
              <CardDescription>Latest credit activity</CardDescription>
            </div>
            <Link to="/admin/billing">
              <Button variant="ghost" size="sm" className="hover:bg-accent/10 hover:text-accent">
                View All
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-2">
              {recentTransactions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No recent transactions
                </p>
              ) : (
                recentTransactions.slice(0, 5).map((tx) => (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between py-3 px-3 rounded-lg bg-gradient-to-r from-muted/30 to-transparent hover:from-muted/50 transition-colors border border-transparent hover:border-muted"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate text-foreground">{tx.user_email}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatActionType(tx.action_type)}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <Badge
                        variant={tx.credits_change > 0 ? "default" : "secondary"}
                        className={tx.credits_change > 0 ? "bg-success text-success-foreground shadow-sm" : "bg-destructive text-destructive-foreground shadow-sm"}
                      >
                        {tx.credits_change > 0 ? "+" : ""}
                        {tx.credits_change}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(tx.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
